import React, {
  useEffect, useMemo, useState,
} from 'react';

import {
  Alert, Grid,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { toInteger } from 'lodash';
import { useSearchParams } from 'react-router-dom';

import ContentCard from '../../components/ContentCard';
import EditField from '../../components/EditField';
import SkeletonStack from '../../components/SkeletonStack';
import Tabs from '../../components/Tabs';
import { useGetClassesQuery } from '../../services/classes';
import { useGetCoursesQuery } from '../../services/courses';
import { useGetExamAttemptsByUserQuery, useGetExamTypesQuery } from '../../services/exams';
import { ExamAttempt } from '../../services/exams/types';
import { useGetStaffsQuery } from '../../services/staff';
import { ShortStaff } from '../../services/staff/types';
import { useGetTutorialsInRangeQuery } from '../../services/tutorials';
import addDays from '../../utils/common/addDays';

import ClassesCard from './ClassesCard';
import CompletedTutorialsCard from './CompletedTutorialsCard';
import {
  FieldName, FieldRow,
} from './index.styles';
import MarkedExamsCard from './MarkedExamsCard';
import PayrollSummaryCard from './PayrollSummaryCard';
import { ExamTally } from './types';
import { getPayrollEndDatetime, getPayrollStartDatetime } from './utils';

const DEFAULT_START_DATETIME = getPayrollStartDatetime();
const DEFAULT_END_DATETIME = getPayrollEndDatetime();

const DEFAULT_STUDENTS_THRESHOLD = 0;
const DEFAULT_WEEKS_THRESHOLD = 2;

const AdminPayrollPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryStaff = searchParams.get('staff');

  const [staffList, setStaffList] = useState<ShortStaff[]>([]);
  const [staffId, setStaffId] = useState<string | undefined>(queryStaff || undefined);
  const [markedExams, setMarkedExams] = useState<ExamAttempt[]>([]);

  const [startDatetime, setStartDatetime] = useState<Dayjs>(DEFAULT_START_DATETIME);
  const [endDatetime, setEndDatetime] = useState<Dayjs>(DEFAULT_END_DATETIME);

  const [studentsThreshold, setStudentsThreshold] = useState<number>(DEFAULT_STUDENTS_THRESHOLD);
  const [weeksThreshold, setWeeksThreshold] = useState<number>(DEFAULT_WEEKS_THRESHOLD);

  const [examTally, setExamTally] = useState<ExamTally>({});
  const [markingDuration, setMarkingDuration] = useState<number>(0);
  const [tutorialDuration, setTutorialDuration] = useState<number>(0);

  // rawStaffList is the unsorted data, staffList is the sorted
  const { data: rawStaffList } = useGetStaffsQuery({ excludeDisabled: true });

  // Fetch exam types
  const { data: examTypes, isLoading: isExamTypesLoading } = useGetExamTypesQuery({});

  // Fetch marked exams
  const {
    data: exams, isLoading: isExamsLoading, isFetching: isExamsFetching,
  } = useGetExamAttemptsByUserQuery({ marker: staffId }, { skip: !staffId });

  // Fetch tutorials
  const {
    data: tutorials, isLoading: isTutorialsLoading, isFetching: isTutorialsFetching,
  } = useGetTutorialsInRangeQuery({
    startDatetime: startDatetime.valueOf(), endDatetime: endDatetime.valueOf(), host: staffId,
  }, { skip: !staffId });

  // Fetch classes
  const {
    data: classes, isLoading: isClassesLoading, isFetching: isClassesFetching,
  } = useGetClassesQuery({ teacher: staffId, includeNextLessonDatetime: true });

  // Fetch courses from courses API
  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});

  const allClasses = useMemo(() => (classes?.data || []).map((c) => {
    const dayLabel = c.day === -1 ? 'Undetermined' : dayjs().day(c.day).format('dddd');
    const timeLabel = dayjs(c.time, 'HHmm').format('h:mm a');

    return ({
      ...c,
      dayLabel,
      timeLabel,
      dayTimeLabel: `${dayLabel} ${timeLabel}`,
    });
  }), [classes]);

  const validClasses = useMemo(() => allClasses.filter((c) => (
    c.studentCount > studentsThreshold
    && c.nextLessonDatetime !== null
    && dayjs(c.nextLessonDatetime).isBefore(dayjs().add(weeksThreshold, 'week'))
  )) || [], [allClasses, studentsThreshold, weeksThreshold]);

  // Sort staff list on fetch
  useEffect(() => {
    if (rawStaffList?.data) {
      const sortedStaffList = [...rawStaffList.data]
        .sort((a, b) => (a.name).localeCompare(b.name));
      setStaffList(sortedStaffList);

      if (!staffId) {
        setStaffId(sortedStaffList?.[0].staffId || '');
      }
    }
  },
  [rawStaffList]);

  // Update the payroll end date when start date is changed
  useEffect(() => {
    setEndDatetime(addDays(startDatetime, 13).endOf('day'));
  }, [startDatetime]);

  // Filter out marking efforts in progress and limit listed exams to
  // those within the current payroll period
  useEffect(() => {
    if (exams) {
      // Exams currently being marked have a marked datetime of infinity for sorting purposes
      // We only want the exams which have been fully marked for payroll
      const startDatetimeInMs = startDatetime.valueOf();
      const endDatetimeInMs = endDatetime.valueOf();
      setMarkedExams(exams?.data
        .filter((e) => e?.markedDatetime &&
        e.markedDatetime >= startDatetimeInMs &&
        e.markedDatetime < endDatetimeInMs),
      );
    }
  }, [exams, startDatetime, endDatetime]);

  // Update exam tally
  useEffect(() => {
    // Tally markedExams by grouping them by exam type
    if (markedExams && examTypes && examTypes?.data.length > 0) {
      const tally: ExamTally = examTypes?.data.reduce((acc, curr) => ({
        ...acc,
        [`${curr.subject}#${curr.type}`]: {
          type: curr.type,
          label: curr.label,
          count: 0,
          unitPayDuration: curr.unitPayDuration,
          unitPayRate: curr.unitPayRate,
        },
      }), {});

      // Tally exams by type
      markedExams.forEach((exam) => {
        const examType = examTypes?.data.find((e) => e.type === exam.type);
        if (examType) {
          tally[`${exam.subject}#${exam.type}`].count++;
        }
      });

      // Remove exam types with a zero count
      Object.keys(tally).forEach((key) => {
        if (tally[key].count === 0) {
          delete tally[key];
        }
      });

      // Tally total duration of marking work
      const totalMarkingDuration = Object.values(tally).reduce((acc, curr) => (
        acc + curr.count * curr.unitPayDuration
      ), 0);

      setExamTally(tally);
      setMarkingDuration(totalMarkingDuration);
    }
  }, [markedExams, examTypes]);

  // Update total tutorial duration
  useEffect(() => {
    if (tutorials) {
      setTutorialDuration(tutorials?.data.reduce((acc, curr) => {
        // Check whether the tutorial took place (ie. there are attendees)
        if ((curr?.attendees || []).length > 0) {
          // Add the duration of the tutorial to the total tutorial duration
          return acc + curr.duration;
        }
        return acc;
      }, 0));
    }
  }, [tutorials]);

  if (
    !rawStaffList
    || !markedExams
    || isExamsLoading
    || isTutorialsLoading
    || isClassesLoading
    || isExamTypesLoading
    || isCoursesLoading
  ) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <>
      <Tabs
        options={staffList
          ?.map((staff) => ({ key: staff.staffId, label: staff.name })) || []}
        selectedKey={staffId || ''}
        onChange={(key) => {
          setSearchParams({ staff: key }, { replace: true });
          setStaffId(key);
        }}
        desktopDropdown
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ContentCard variant="contained" title="Payroll Dates">
            <>
              <FieldRow>
                <FieldName variant="subtitle2">Start Date</FieldName>
                <EditField
                  type="date"
                  value={startDatetime.valueOf()}
                  onSubmit={(newDatetime) => setStartDatetime(dayjs(toInteger(newDatetime)))}
                />
              </FieldRow>
              <FieldRow>
                <FieldName variant="subtitle2">End Date</FieldName>
                <EditField
                  type="date"
                  value={endDatetime.valueOf()}
                  onSubmit={(newDatetime) => setEndDatetime(dayjs(toInteger(newDatetime)).endOf('day'))}
                />
              </FieldRow>
            </>
          </ContentCard>
        </Grid>
        <Grid item xs={12} md={8}>
          <ContentCard variant="contained" title="Class Validity Criteria">
            <FieldRow>
              <FieldName variant="subtitle2">More than... (students)</FieldName>
              <EditField
                type="number"
                value={studentsThreshold}
                onSubmit={(newValue) => setStudentsThreshold(newValue as number)}
              />
            </FieldRow>
            <FieldRow>
              <FieldName variant="subtitle2">A lesson within... (weeks)</FieldName>
              <EditField
                type="number"
                value={weeksThreshold}
                onSubmit={(newValue) => setWeeksThreshold(newValue as number)}
              />
            </FieldRow>
          </ContentCard>
        </Grid>
        {(startDatetime.valueOf() !== DEFAULT_START_DATETIME.valueOf()
        || endDatetime.valueOf() !== DEFAULT_END_DATETIME.valueOf()) && (
          <Grid item xs={12}>
            <Alert severity="warning">
              You are currently viewing the payroll data for a custom period.
            </Alert>
          </Grid>
        )}
        {isClassesFetching || isExamsFetching || isTutorialsFetching ? (
          <Grid item xs={12}>
            <SkeletonStack />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} md={6}>
              <PayrollSummaryCard
                validClasses={validClasses}
                examTally={examTally}
                markingDuration={markingDuration}
                tutorialDuration={tutorialDuration}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ClassesCard
                courses={courses?.data || []}
                allClasses={allClasses}
                validClasses={validClasses}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MarkedExamsCard
                markedExams={markedExams}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CompletedTutorialsCard
                tutorials={tutorials?.data || []}
                courses={courses?.data || []}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default AdminPayrollPage;
